import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Container,
  Paper,
  Avatar,
  Chip,
  IconButton,
  CircularProgress,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { taskService } from '../services/taskService';
import { taskStatusService, TaskStatus } from '../services/taskStatusService';
import api from '../services/api';

interface TaskCard {
  id: string;
  title: string;
  description?: string;
  statusId: string;
  statusName: string;
  priority: number | string;
  assignee?: { id: string; firstName: string; lastName: string };
  dueDate?: string;
  projectId: string;
  projectKey: string;
  labels: { id: string; name: string; color?: string }[];
  taskTypeName?: string;
}

const ITEM_TYPE = 'TASK_CARD';

const getPriorityLabel = (p: string | number) => {
  const map: Record<string, string> = { '1': 'Low', '2': 'Medium', '3': 'High', '4': 'Urgent' };
  return map[String(p)] || String(p);
};

const getPriorityColor = (p: string | number) => {
  switch (String(p)) {
    case '4': return '#EF4444';
    case '3': return '#F59E0B';
    case '2': return '#3B82F6';
    case '1': return '#10B981';
    default: return '#6B7280';
  }
};

// Draggable Task Card Component
const DraggableTaskCard: React.FC<{ task: TaskCard; onClick: () => void }> = ({ task, onClick }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { id: task.id, statusId: task.statusId },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  return (
    <Paper
      ref={drag}
      elevation={isDragging ? 4 : 1}
      onClick={onClick}
      sx={{
        p: 1.5,
        mb: 1,
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1,
        borderLeft: `3px solid ${getPriorityColor(task.priority)}`,
        '&:hover': { elevation: 3, bgcolor: 'action.hover' },
        transition: 'box-shadow 0.2s',
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
        {task.title}
      </Typography>

      {task.labels.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
          {task.labels.map((l) => (
            <Chip key={l.id} label={l.name} size="small"
              sx={{ height: 18, fontSize: '0.65rem', bgcolor: l.color || '#e0e0e0', color: '#fff' }} />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Chip label={getPriorityLabel(task.priority)} size="small"
            sx={{ height: 20, fontSize: '0.65rem', bgcolor: getPriorityColor(task.priority), color: '#fff' }} />
          {task.taskTypeName && (
            <Chip label={task.taskTypeName} size="small" variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem' }} />
          )}
        </Box>
        {task.assignee ? (
          <Tooltip title={`${task.assignee.firstName} ${task.assignee.lastName}`}>
            <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>
              {task.assignee.firstName[0]}
            </Avatar>
          </Tooltip>
        ) : (
          <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: '#e0e0e0' }}>?</Avatar>
        )}
      </Box>

      {task.dueDate && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Due: {new Date(task.dueDate).toLocaleDateString()}
        </Typography>
      )}
    </Paper>
  );
};

// Droppable Column Component
const KanbanColumn: React.FC<{
  status: TaskStatus;
  tasks: TaskCard[];
  onDrop: (taskId: string, newStatusId: string) => void;
  onTaskClick: (task: TaskCard) => void;
  onAddTask: (statusId: string) => void;
}> = ({ status, tasks, onDrop, onTaskClick, onAddTask }) => {
  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    drop: (item: { id: string; statusId: string }) => {
      if (item.statusId !== status.id) {
        onDrop(item.id, status.id);
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  return (
    <Box
      ref={drop}
      sx={{
        minWidth: 280,
        maxWidth: 320,
        flex: '0 0 300px',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isOver ? 'action.hover' : 'grey.50',
        borderRadius: 2,
        overflow: 'hidden',
        height: 'fit-content',
        maxHeight: 'calc(100vh - 220px)',
      }}
    >
      {/* Column Header */}
      <Box sx={{
        p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: `3px solid ${status.color || '#6B7280'}`,
        bgcolor: 'background.paper',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {status.name}
          </Typography>
          <Chip label={tasks.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
        </Box>
        <IconButton size="small" onClick={() => onAddTask(status.id)}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Task Cards */}
      <Box sx={{ p: 1, overflowY: 'auto', flexGrow: 1 }}>
        {tasks.map((task) => (
          <DraggableTaskCard key={task.id} task={task} onClick={() => onTaskClick(task)} />
        ))}
        {tasks.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No tasks
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Main Kanban Board Page
const ProjectBoardPage: React.FC = () => {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const [tasks, setTasks] = useState<TaskCard[]>([]);
  const [statuses, setStatuses] = useState<TaskStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAssignee, setFilterAssignee] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [projectName, setProjectName] = useState('');

  // Quick add task dialog
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddStatusId, setQuickAddStatusId] = useState('');
  const [quickAddTitle, setQuickAddTitle] = useState('');

  const loadData = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const [statusesData, tasksData, projectData] = await Promise.all([
        taskStatusService.getTaskStatuses(),
        taskService.getTasks({ projectId }),
        api.get(`/projects/${projectId}`).then(r => r.data),
      ]);
      setStatuses(statusesData.sort((a, b) => a.position - b.position));
      setTasks(tasksData as any);
      setProjectName(projectData.name || '');
    } catch (err) {
      console.error('Failed to load board data:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDrop = async (taskId: string, newStatusId: string) => {
    // Optimistic update
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, statusId: newStatusId } : t));

    try {
      await taskService.updateTask(taskId, { statusId: newStatusId });
    } catch (err) {
      console.error('Failed to update task status:', err);
      loadData(); // Revert on error
    }
  };

  const handleTaskClick = (task: TaskCard) => {
    navigate(`/tasks/${task.id}`);
  };

  const handleQuickAdd = async () => {
    if (!quickAddTitle.trim() || !projectId) return;
    try {
      const defaultType = await api.get('/tasktypes').then(r => {
        const types = r.data;
        return types.find((t: any) => t.isDefault)?.id || types[0]?.id;
      });

      await taskService.createTask({
        projectId,
        title: quickAddTitle,
        priority: 2, // Medium
        taskTypeId: defaultType,
        statusId: quickAddStatusId,
      } as any);

      setQuickAddTitle('');
      setQuickAddOpen(false);
      loadData();
    } catch (err) {
      console.error('Failed to create task:', err);
    }
  };

  const filteredTasks = tasks.filter((task) => {
    const matchesSearch = !searchTerm ||
      task.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAssignee = filterAssignee === 'all' ||
      task.assignee?.id === filterAssignee;
    const matchesPriority = filterPriority === 'all' ||
      String(task.priority) === filterPriority;
    return matchesSearch && matchesAssignee && matchesPriority;
  });

  const uniqueAssignees = Array.from(
    new Map(tasks.filter(t => t.assignee).map(t => [t.assignee!.id, t.assignee!])).values()
  );

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ px: 3, pt: 2, pb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {projectName} Board
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredTasks.length} tasks across {statuses.length} columns
            </Typography>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Assignee</InputLabel>
            <Select value={filterAssignee} label="Assignee" onChange={(e) => setFilterAssignee(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              {uniqueAssignees.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.firstName} {a.lastName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={filterPriority} label="Priority" onChange={(e) => setFilterPriority(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="4">Urgent</MenuItem>
              <MenuItem value="3">High</MenuItem>
              <MenuItem value="2">Medium</MenuItem>
              <MenuItem value="1">Low</MenuItem>
            </Select>
          </FormControl>
        </Paper>

        {/* Kanban Columns */}
        <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
          {statuses.map((status) => (
            <KanbanColumn
              key={status.id}
              status={status}
              tasks={filteredTasks.filter((t) => t.statusId === status.id)}
              onDrop={handleDrop}
              onTaskClick={handleTaskClick}
              onAddTask={(statusId) => {
                setQuickAddStatusId(statusId);
                setQuickAddOpen(true);
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Quick Add Task Dialog */}
      <Dialog open={quickAddOpen} onClose={() => setQuickAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Task</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Task Title"
            value={quickAddTitle}
            onChange={(e) => setQuickAddTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleQuickAdd()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickAddOpen(false)}>Cancel</Button>
          <Button onClick={handleQuickAdd} variant="contained" disabled={!quickAddTitle.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </DndProvider>
  );
};

export default ProjectBoardPage;
